import api from './api';

export type AgentRuntimeStatus = 'online' | 'offline' | 'unknown';

export interface LoadedAgent {
  id: string;
  agentCode: string;
  name: string;
  description: string;
  category?: string;
  subCategory?: string;
  version?: string;
  developer?: string;
  status?: AgentRuntimeStatus;
  capabilities?: {
    inputTypes?: string[];
    outputTypes?: string[];
    streaming?: boolean;
  };
  permissions?: string[];
}

export interface AgentMessage {
  id: string;
  role: 'user' | 'agent' | 'system';
  type: 'text' | 'markdown' | 'json' | 'table' | 'html' | 'form' | 'file' | 'task' | 'error';
  content: any;
  createdAt: string;
  status?: 'sending' | 'sent' | 'failed';
  sourceTaskId?: string;
}

export interface AgentConversation {
  id: string;
  title: string;
  agentCode: string;
  updatedAt: string;
}

export interface SendAgentMessagePayload {
  agentCode: string;
  conversationId?: string;
  messages: Array<{ role: 'user' | 'assistant' | 'system'; content: string }>;
  content: string;
}

export interface SendAgentMessageResult {
  conversationId?: string;
  response: AgentMessage;
}

export interface AgentArtifactCategory {
  id: string;
  agentCode: string;
  name: string;
  description?: string;
  category?: string;
  subCategory?: string;
  artifactCount: number;
}

export interface AgentArtifactRecord {
  id: string;
  conversationId: string;
  agentCode: string;
  agentName: string;
  agentDescription?: string;
  taskId?: string;
  title: string;
  command: string;
  status: 'running' | 'completed' | 'failed';
  stepText: string;
  result: string;
  resultKind: 'text' | 'markdown' | 'html';
  files?: Array<{ fileId?: string; fileName?: string; format?: string }>;
  error?: string;
  startedAt: string;
  finishedAt?: string;
  updatedAt: string;
}

export interface AgentArtifactsResult {
  agents: AgentArtifactCategory[];
  items: AgentArtifactRecord[];
}

const now = () => new Date().toISOString();

const NON_AGENT_NAME_FRAGMENTS = [
  'W' + 'ord',
  String.fromCharCode(26263, 26631),
  String.fromCharCode(21512, 35268, 26816, 26597, 22120),
  ['word', 'blind', 'bid', 'checker'].join('-'),
];

function containsNonAgentFragment(text: string): boolean {
  return NON_AGENT_NAME_FRAGMENTS.some(fragment => text.toLowerCase().includes(fragment.toLowerCase()));
}

function isRealLoadedAgent(raw: any): boolean {
  if (!raw || typeof raw !== 'object') return false;

  const typeText = String(raw.type || raw.kind || raw.componentType || raw.category || '').toLowerCase();
  if (/tool|component|plugin|组件|工具/.test(typeText)) return false;

  const text = `${raw.name || ''} ${raw.title || ''} ${raw.agentCode || ''} ${raw.agent_code || ''} ${raw.code || ''} ${raw.slug || ''}`;
  if (containsNonAgentFragment(text)) return false;

  const code = raw.agentCode || raw.agent_code || raw.code || raw.slug;
  const hasAgentIdentity = Boolean(code || raw.agentId || raw.agent_id || raw.runtimeId || raw.runtime_id);
  const markedAsAgent = /agent|智能体|智件/.test(`${typeText} ${raw.category || ''} ${raw.tags || ''}`);
  return hasAgentIdentity || markedAsAgent;
}

function normalizeAgent(raw: any, index: number): LoadedAgent {
  const code = raw.agentCode || raw.agent_code || raw.code || raw.slug || raw.id || raw.uuid || `agent-${index + 1}`;
  return {
    id: String(raw.id || raw.uuid || code),
    agentCode: String(code),
    name: String(raw.name || raw.title || raw.displayName || code),
    description: String(raw.description || raw.summary || '已加载到当前工作区，可通过自然语言调用。'),
    category: raw.category || raw.type || '已加载智件',
    subCategory: raw.subCategory || raw.sub_category,
    version: raw.version || '1.0.0',
    developer: raw.developer?.name || raw.developer || raw.author || '未知开发者',
    status: raw.status || 'unknown',
    capabilities: {
      inputTypes: raw.capabilities?.input_types || raw.capabilities?.inputTypes || ['text'],
      outputTypes: raw.capabilities?.output_types || raw.capabilities?.outputTypes || ['text', 'markdown'],
      streaming: Boolean(raw.capabilities?.streaming),
    },
    permissions: raw.required_permissions || raw.permissions || [],
  };
}

export async function listLoadedAgents(): Promise<LoadedAgent[]> {
  const candidates = ['/agents/my', '/agent-runtime/agents'];
  for (const url of candidates) {
    try {
      const { data } = await api.get(url);
      const rows = Array.isArray(data) ? data : data?.items || data?.data || data?.agents || [];
      if (Array.isArray(rows) && rows.length > 0) {
        const normalized = rows.filter(isRealLoadedAgent).map(normalizeAgent);
        if (normalized.length === 0) continue;
        return normalized.sort((a, b) => {
          const aOps = /运维|监控|告警|ops|operation/i.test(`${a.name} ${a.agentCode} ${a.category || ''} ${a.subCategory || ''}`) ? 0 : 1;
          const bOps = /运维|监控|告警|ops|operation/i.test(`${b.name} ${b.agentCode} ${b.category || ''} ${b.subCategory || ''}`) ? 0 : 1;
          return aOps - bOps;
        });
      }
    } catch {
      // Keep trying compatible agent-runtime endpoints; do not fall back to tool/component APIs.
    }
  }
  return [];
}

/** AI 指挥中心刷新恢复及产物记录使用的持久化智能体任务。 */
export async function listAgentArtifacts(takePerAgent = 30): Promise<AgentArtifactsResult> {
  const { data } = await api.get('/agents/artifacts', { params: { take: takePerAgent } });
  return {
    agents: Array.isArray(data?.agents) ? data.agents : [],
    items: Array.isArray(data?.items) ? data.items : [],
  };
}

export async function listConversations(agentCode: string): Promise<AgentConversation[]> {
  try {
    const { data } = await api.get(`/agent-runtime/${agentCode}/conversations`);
    const rows = Array.isArray(data) ? data : data?.items || [];
    return rows;
  } catch {
    return [{ id: `local-${agentCode}`, title: '新的智能体会话', agentCode, updatedAt: now() }];
  }
}

export async function listMessages(agentCode: string, conversationId: string): Promise<AgentMessage[]> {
  if (conversationId.startsWith('local-')) return [];
  try {
    const { data } = await api.get(`/agent-runtime/${agentCode}/conversations/${conversationId}/messages`);
    return Array.isArray(data) ? data : data?.items || [];
  } catch {
    return [];
  }
}

export async function getAgentTask(agentCode: string, taskId: string): Promise<any> {
  const { data } = await api.get(`/agent-runtime/${agentCode}/tasks/${taskId}`);
  return data;
}

export async function getAgentFileText(agentCode: string, fileId: string): Promise<string> {
  const { data } = await api.get(`/agent-runtime/${agentCode}/files/${encodeURIComponent(fileId)}`, {
    responseType: 'text',
  });
  return typeof data === 'string' ? data : String(data || '');
}

export async function sendAgentMessage(payload: SendAgentMessagePayload): Promise<SendAgentMessageResult> {
  try {
    const { data } = await api.post(`/agent-runtime/${payload.agentCode}/conversations/${payload.conversationId || 'new'}/messages`, {
      conversationId: payload.conversationId,
      content: payload.content,
      messages: payload.messages,
    }, {
      // 必须大于后端对智能体的 180s fetch 超时（另加 manifest 解析和落库耗时），
      // 否则前端总是先超时：用户看到报错，而后端稍后把智能体回复落库，刷新后"结果又出来了"。
      timeout: 200000,
    });
    const body = data?.response || data?.message || data;
    const nextConversationId = data?.conversationId || payload.conversationId;
    return {
      conversationId: nextConversationId,
      response: {
        id: body?.id || `agent-${Date.now()}`,
        role: 'agent',
        type: body?.type || data?.type || 'markdown',
        content: body?.content ?? body?.result ?? data?.result ?? '智能体已返回结果。',
        createdAt: body?.createdAt || now(),
      },
    };
  } catch (error: any) {
    const reason = error?.response?.data?.message || error?.message || '当前后端智能体网关调用失败';
    throw new Error(reason);
  }
}
